import { useState, useEffect } from "react";
import { axiosInstance } from "../utils/axios";
import useModalStore from "../stores/useModalStore";
import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import useDialogStore from "../stores/useDialogStore";
import DragDropUpload from "./DragDropUpload";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Button from "./Button";

export default function ImportCountersPdfModal({ onSuccess }) {
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [importData, setImportData] = useState(null);
  const { setShowLoading } = useModalStore();
  const { closeDialog } = useDialogStore();
  const queryClient = useQueryClient();

  const importCountersPdfMutation = useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axiosInstance.post(
        `${window.REACT_APP_API_ENDPOINT}/device-rentals/import-pdf-counter`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setImportData(data);

      if (data.errors && data.errors.length > 0) {
        const errorMessage = (
          <div className="mb-4">
            <p className="text-green-600 font-semibold mb-2">{data.message}</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-600">
                <p className="font-semibold mb-2">Row Errors:</p>
                <ul className="list-disc list-inside space-y-1 max-h-60 overflow-y-auto">
                  {data.errors.map((error, index) => (
                    <li key={index} className="text-sm">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
        setMessage(errorMessage);
      } else {
        setMessage("");
        toast.success(data.message);
      }

      // Always refresh the table on success, even if there are partial errors
      queryClient.invalidateQueries(["device-rentals"]);

      // Notify parent with full response data (e.g. inactive_rentals warnings)
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: (error) => {
      const errorMessage = (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full mb-4">
          <div className="text-red-600">
            <p className="font-semibold mb-2">Import Failed:</p>
            <p className="text-sm">
              {error.response?.data?.error || "An unexpected error occurred."}
            </p>
          </div>
        </div>
      );
      setMessage(errorMessage);
      toast.error("PDF counter import failed. Please try again.");
    },
  });

  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setMessage("");
    setImportData(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setMessage("Please select a file.");
      return;
    }

    setShowLoading(true);
    importCountersPdfMutation.mutate(file, {
      onSettled: () => {
        setShowLoading(false);
      },
    });
  };

  const handleClearFile = () => {
    setFile(null);
    setMessage("");
    setImportData(null);
  };

  useEffect(() => {
    setImportData(null);
    setMessage("");
  }, [file]);

  const isUploading = importCountersPdfMutation.isPending;

  return (
    <div className="relative">
      <form onSubmit={handleSubmit}>
        <div className="w-full flex flex-col justify-between items-center py-3 px-4 bg-white rounded-lg">
          <div className="w-full flex flex-col gap-4 mb-4">
            <div className="flex items-center justify-center w-full">
              <DragDropUpload
                onFileSelect={handleFileSelect}
                accept=".pdf"
                maxSize={10 * 1024 * 1024} // 10MB
                className="h-32"
                hasFile={!!file}
              >
                {!file ? (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 text-red-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF files only (.pdf)</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 text-green-500"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <p className="mb-2 text-sm font-semibold text-green-600">
                      File selected
                    </p>
                    <p className="text-xs text-gray-500">
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </p>
                    <p
                      className="text-xs text-blue-500 mt-2 cursor-pointer hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearFile();
                      }}
                    >
                      Change file
                    </p>
                  </div>
                )}
              </DragDropUpload>
            </div>
          </div>

          {/* Success summary */}
          {importData && !message && (
            <div className="w-full mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700 font-semibold text-sm">{importData.message}</p>
              <p className="text-green-600 text-xs mt-1">
                {importData.results?.length} row(s) updated successfully.
              </p>
            </div>
          )}

          {/* Error / partial error message */}
          {message && (
            <div className="w-full">
              {typeof message === "string" ? (
                <p className="text-sm font-semibold w-full">{message}</p>
              ) : (
                message
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 w-full">
            <Button
              variant="outline-light"
              size="sm"
              onClick={closeDialog}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              icon={<ArrowUpTrayIcon className="w-4 h-4" />}
              disabled={!file || isUploading}
              loading={isUploading}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
